import { useState, useCallback, SyntheticEvent } from "react"
import { useTranslation } from "react-i18next"
import { RiFileDownloadLine } from "@remixicon/react"

import BasicButton from "@/components/button/BasicButton"
import Selector from "@/components/form/selector/Selector"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import Checkbox from "@/components/form/checkbox/Checkbox"
import TextField from "@/components/form/textField/TextField"
import Dialog from "@/components/dialog/Dialog"
import { LinearProgress, Box, Typography } from "@mui/material"

import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import { createGlobalStore } from "@/stores/global"
import type { PdfExportOptions } from "@/types/pdfBuilder"
import { exportPagesToPdf, exportPagesToPng } from "../utils/captureReportPdf"

interface Option {
  value: string
  label: string
}

export default function ExportToolbar() {
  const { t } = useTranslation()
  const { setSnackBar } = createGlobalStore()

  const qualityOptions = [
    { value: "low", label: t("qualityLow", "Low (faster)") },
    { value: "medium", label: t("qualityMedium", "Medium") },
    { value: "high", label: t("qualityHigh", "High (recommended)") },
  ]

  const pages = usePdfBuilderStore((s) => s.pages)
  const isExporting = usePdfBuilderStore((s) => s.isExporting)
  const exportProgress = usePdfBuilderStore((s) => s.exportProgress)

  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportFormat, setExportFormat] = useState<"pdf" | "png">("pdf")
  const [exportOptions, setExportOptions] = useState<PdfExportOptions>({
    filename: "report",
    quality: "high",
    includePageNumbers: true,
  })

  const handleExportClick = useCallback(() => {
    setExportDialogOpen(true)
  }, [])

  const handleExportCancel = useCallback(() => {
    setExportDialogOpen(false)
  }, [])

  const handleExport = async () => {
    setExportDialogOpen(false)

    const exportOne = exportFormat === "pdf" ? exportPagesToPdf : exportPagesToPng

    try {
      await exportOne({
        filename: exportOptions.filename || "report",
        quality: exportOptions.quality || "high",
        includePageNumbers: exportOptions.includePageNumbers ?? true,
      })
    } catch (error) {
      // 여기서 알리지 않으면 진행 창만 닫히고 끝나 사용자는 버튼이 안 먹는다고 느낀다
      console.error(`${exportFormat.toUpperCase()} export failed:`, error)
      setSnackBar({
        open: true,
        message: t("messages.failBasic", "Something went wrong."),
        severity: "error",
      })
    }
  }

  return (
    <>
      <div className="ExportToolbar">
        <BasicButton
          type="primary-contained"
          size="sm"
          onClick={handleExportClick}
          disabled={isExporting || pages.every((p) => p.items.length === 0)}>
          <RiFileDownloadLine size={16} />
          {t("exportPdf", "Export PDF")}
        </BasicButton>
      </div>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleExportCancel}
        title={t("exportReport", "Export Report")}
        /* 넓은 화면에서 팝업이 통째로 늘어나지 않게 상한을 둔다 — Dialog 는 CSS 길이를 그대로 쓴다 (2026-10-02 요청) */
        maxWidth="600px"
        footer={
          <div className="ExportDialog__footer">
            <BasicButton type="default-outlined" size="md" onClick={handleExportCancel}>
              {t("button.cancel", "Cancel")}
            </BasicButton>
            <BasicButton type="primary-contained" size="md" onClick={handleExport}>
              {t("export", "Export")}
            </BasicButton>
          </div>
        }>
        <div className="ExportDialog__content">
          <div className="ExportDialog__field">
            <label className="ExportDialog__label">{t("filename", "File Name")}</label>
            <TextField
              fullWidth
              value={exportOptions.filename}
              onChange={(e) => setExportOptions((prev) => ({ ...prev, filename: e.target.value }))}
            />
          </div>

          <div className="ExportDialog__row">
            <div className="ExportDialog__field ExportDialog__field--flex">
              <label className="ExportDialog__label">{t("quality", "Quality")}</label>
              <Selector<Option>
                value={
                  qualityOptions.find((o) => o.value === (exportOptions.quality || "high")) ??
                  qualityOptions[2]
                }
                options={qualityOptions}
                labelKey="label"
                onChange={(_: SyntheticEvent, option: Option) => {
                  if (option) {
                    setExportOptions((prev) => ({
                      ...prev,
                      quality: option.value as "low" | "medium" | "high",
                    }))
                  }
                }}
              />
            </div>
            <Checkbox
              label={t("includePageNumbers", "Include page numbers")}
              checked={exportOptions.includePageNumbers}
              onCheckboxChange={(_, checked) =>
                setExportOptions((prev) => ({ ...prev, includePageNumbers: checked }))
              }
            />
          </div>

          <div className="ExportDialog__field">
            <label className="ExportDialog__label">{t("format", "Format")}</label>
            <RadioGroup
              items={[
                { value: "pdf", label: "PDF" },
                { value: "png", label: t("pngImage", "PNG Image") },
              ]}
              direction="row"
              value={exportFormat}
              onChange={(_, value) => {
                if (value) setExportFormat(value as "pdf" | "png")
              }}
            />
          </div>
        </div>
      </Dialog>

      {/* Export Progress Dialog */}
      <Dialog
        open={isExporting}
        onClose={() => {}}
        title={t("exporting", "Exporting...")}
        maxWidth="600px">
        <div className="ExportDialog__progress">
          <Box sx={{ width: "100%", mt: 2 }}>
            <LinearProgress variant="determinate" value={exportProgress} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {Math.round(exportProgress)}%
          </Typography>
        </div>
      </Dialog>
    </>
  )
}
